/***************************************************************************
 *
 * Copyright 2025 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ***************************************************************************/
import { Vector3 } from '@babylonjs/core/Maths/math.vector';
import { CreateHotSpotFromCamera, Viewer } from '@babylonjs/viewer';
import { computeMaxExtents } from '@babylonjs/core';
import { Observable } from '@babylonjs/core/Misc/observable';
/**
 * @internal
 */
export const getModelCameraSelector = (model, cameraName) => {
    return model.assetContainer.cameras.find((camera) => {
        return camera.name === cameraName || camera.parent?.name === cameraName;
    });
};
const DEFAULT_RENDERER_OPTIONS = {
    aoStrength: {
        type: 'slider',
        value: 0.2,
        min: 0,
        max: 1,
        step: 0.01,
    },
};
export class A3dViewer extends Viewer {
    constructor() {
        super(...arguments);
        this.disableInterpolate = false;
        this._forceRender = false;
        /**
         * If true, the camera will automatically reframe around the models when they are added or removed.
         */
        this._cameraReframe = true;
        /**
         * If true, the camera will not have any limits on its movement.
         */
        this._cameraLimit = true;
        this._rendererOptions = DEFAULT_RENDERER_OPTIONS;
        /**
         * Observable that fires when rendererOptions change.
         */
        this.onRendererOptionsChanged = new Observable();
    }
    /**
     * Indicates if the scene has been mutated to trigger a re-render
     * useful when the scene is mutated outside of the viewer and the rendering is suspended
     */
    markSceneMutated() {
        this._markSceneMutated();
    }
    /**
     * Add a model to the viewer.
     * Will trigger a modelchange event
     * @param source - The source of the model to load.
     * @param options - The options to use when loading the model.
     * @returns The loaded model.
     */
    async addModel(source, options) {
        const model = await this._loadModel(source, options);
        if (this.cameraLimit) {
            this._updateCameraLimits();
        }
        this.onModelChanged.notifyObservers(source);
        return model;
    }
    async removeModel(model) {
        if (!this._loadedModels.includes(model)) {
            console.warn('Model not found in the viewer');
            return;
        }
        this._loadedModels.slice(this._loadedModels.indexOf(model), 1);
        model.dispose();
        if (this.cameraLimit) {
            this._updateCameraLimits();
        }
    }
    /**
     * Reframe the camera around all models on the scene
     * @param interpolateCamera - If true, the camera will interpolate to the new position
     */
    frameCamera(interpolateCamera) {
        // only reframe the camera if autoReframe is enabled or if this is the first model loaded
        if (this.cameraReframe) {
            if (this.disableInterpolate) {
                this._reframeCamera(false);
            }
            else {
                this._reframeCamera(interpolateCamera);
            }
        }
    }
    _reframeCamera(interpolateCamera) {
        super._reframeCamera(interpolateCamera);
        // Set the wheel precision to be the inverse of the radius like in Babylon.js framing behavior
        // After _reframeCamera the radius is based on the distance to the models so the wheel precision will depend on the scene size
        this._camera.wheelPrecision = 100 / this._camera.radius;
    }
    /**
     * Returns whether a world hot spot was found and updates the result with world and canvas coordinates, as well as visibility status.
     * @param query Position and normal information to query the hot spot positions.
     * @param result Reference to be updated with the hot spot coordinates and visibility.
     * @returns True if a hot spot was found, otherwise false.
     */
    getWorldHotSpotToRef(query, result) {
        return this._getHotSpotToRef(null, query, result);
    }
    /**
     * Return the list of currently loaded models.
     * Note that only models loaded through the addModel method are tracked.
     * @returns The list of loaded models.
     */
    loadedModels() {
        return this._loadedModels;
    }
    /**
     * Helper method to get all cameras in the viewer related to the loaded models
     * @returns The list of cameras
     */
    cameras() {
        return this._loadedModels.flatMap((model, index) => model.assetContainer.cameras.flatMap((camera) => ({
            model,
            camera,
            cameraName: camera.parent?.name,
            cameraId: camera.id,
            index,
        })));
    }
    /**
     * Focus the camera on a specific camera in a model
     * @param cameraName - The name of the camera to focus on
     * @param modelIndex - The index of the model in the loadedModels list
     * @param interpolate - If true, the camera will interpolate to the new position
     * @returns True if the camera was found and focused, otherwise false
     */
    async focusCamera(cameraName, modelIndex, interpolate = true) {
        const model = this.loadedModels()[modelIndex ?? 0];
        const camera = getModelCameraSelector(model, cameraName);
        if (!camera) {
            return false;
        }
        const cameraHotspot = await CreateHotSpotFromCamera(model, camera);
        if (cameraHotspot.type === 'world') {
            this._camera.interpolateTo(cameraHotspot.cameraOrbit?.[0], cameraHotspot.cameraOrbit?.[1], cameraHotspot.cameraOrbit?.[2], new Vector3(cameraHotspot.position[0], cameraHotspot.position[1], cameraHotspot.position[2]), undefined, interpolate ? undefined : 0);
            return true;
        }
        return false;
    }
    /**
     * Get the scene of the viewer
     * @returns The scene
     */
    get scene() {
        return this._scene;
    }
    get _shouldRender() {
        return super._shouldRender || this._forceRender;
    }
    /**
     * Set the forceRender flag
     * @param value - The value to set
     */
    set forceRender(value) {
        if (value) {
            console.warn('Rendering is forced. This can lead to performance issues. Please use with caution.');
        }
        else {
            console.warn('Rendering is not forced.');
        }
        this._forceRender = value;
    }
    get cameraReframe() {
        return this._cameraReframe;
    }
    set cameraReframe(value) {
        this._cameraReframe = value;
        if (value) {
            this.frameCamera(true);
        }
    }
    get cameraLimit() {
        return this._cameraLimit;
    }
    set cameraLimit(value) {
        this._cameraLimit = value;
        if (value) {
            this._updateCameraLimits();
        }
        else {
            this._camera.lowerRadiusLimit = null;
            this._camera.upperRadiusLimit = null;
        }
    }
    get rendererOptions() {
        return this._rendererOptions;
    }
    getRendererOption(key) {
        return this._rendererOptions[key];
    }
    setRendererOptions(key, value) {
        if (key in this._rendererOptions) {
            this._rendererOptions[key] = value;
            this.onRendererOptionsChanged.notifyObservers({ key, value });
        }
        else {
            console.warn(`Renderer option ${key} does not exist.`);
        }
    }
    _updateCameraLimits() {
        const { lowerRadiusLimit, upperRadiusLimit, minZ, maxZ } = this._computeLimits();
        this._camera.lowerRadiusLimit = lowerRadiusLimit;
        this._camera.upperRadiusLimit = upperRadiusLimit;
        this._camera.minZ = minZ;
        this._camera.maxZ = maxZ;
    }
    // TODO Copied here until code is available publicly in the Babylon.js viewer
    _computeLimits() {
        let goalRadius = 1;
        let goalTarget = Vector3.Zero();
        const worldBounds = computeModelsBoundingInfos(this._loadedModels);
        if (worldBounds) {
            goalRadius = Vector3.FromArray(worldBounds.size).length() * 1.1;
            goalTarget = Vector3.FromArray(worldBounds.center);
            if (!isFinite(goalRadius)) {
                goalRadius = 1;
                goalTarget.copyFromFloats(0, 0, 0);
            }
        }
        return {
            radius: goalRadius,
            target: goalTarget,
            lowerRadiusLimit: goalRadius * 0.001,
            upperRadiusLimit: goalRadius * 5,
            minZ: goalRadius * 0.001,
            maxZ: goalRadius * 1000,
        };
    }
}
// TODO Copied here until code is available publicly in the Babylon.js viewer
function computeModelsMaxExtents(models) {
    return models.flatMap((model) => {
        return computeMaxExtents(model.assetContainer.meshes, model.assetContainer.animationGroups[model.selectedAnimation]);
    });
}
// TODO Copied here until code is available publicly in the Babylon.js viewer
function reduceMeshesExtendsToBoundingInfo(maxExtents) {
    if (maxExtents.length === 0) {
        return null;
    }
    const min = new Vector3(Math.min(...maxExtents.map((e) => e.minimum.x)), Math.min(...maxExtents.map((e) => e.minimum.y)), Math.min(...maxExtents.map((e) => e.minimum.z)));
    const max = new Vector3(Math.max(...maxExtents.map((e) => e.maximum.x)), Math.max(...maxExtents.map((e) => e.maximum.y)), Math.max(...maxExtents.map((e) => e.maximum.z)));
    const size = max.subtract(min);
    const center = min.add(size.scale(0.5));
    return {
        extents: {
            min: min.asArray(),
            max: max.asArray(),
        },
        size: size.asArray(),
        center: center.asArray(),
    };
}
// TODO Copied here until code is available publicly in the Babylon.js viewer
function computeModelsBoundingInfos(models) {
    const maxExtents = computeModelsMaxExtents(models);
    return reduceMeshesExtendsToBoundingInfo(maxExtents);
}
